import styled from '@emotion/styled/macro';
import {
  Banner,
  Card,
  Modal,
  Snackbar,
  tokens,
  Typography,
} from '@sunrun/experience-ui-components';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  sendProposalEmail,
  sendProposalMessageAndEmail,
} from 'providers/ShareProposal';

const StyledBanner = styled(Banner)`
  &.sr-banner {
    border: none;
    margin-top: 0;
    margin-bottom: 16px;
  }
`;

type SendStatus = 'idle' | 'pending' | 'success' | 'error';

type SharePresentationModalProps = {
  hybridToken: string;
  onDismiss: () => void;
  prospectId: string;
  name: string;
  email: string;
  isAffiliate?: boolean;
};

const SharePresentationModal = ({
  hybridToken,
  onDismiss,
  prospectId,
  name,
  email,
  isAffiliate,
}: SharePresentationModalProps) => {
  const [sendStatus, setSendStatus] = useState<SendStatus>('idle');

  useEffect(() => {
    setSendStatus('idle');
  }, [prospectId, isAffiliate]);

  const handleDismiss = useCallback(() => {
    setSendStatus('idle');
    onDismiss();
  }, [onDismiss]);

  const handleSend = useCallback(async () => {
    setSendStatus('pending');
    try {
      if (isAffiliate) {
        await sendProposalEmail(hybridToken, prospectId, '', 'ONE_PROPOSAL');
      } else {
        await sendProposalMessageAndEmail(
          hybridToken,
          prospectId,
          '',
          'ONE_PROPOSAL',
        );
      }
      setSendStatus('success');
    } catch {
      setSendStatus('error');
    }
  }, [hybridToken, isAffiliate, prospectId]);

  const bannerText = isAffiliate
    ? `${name} can download the Sunrun Customer App to view their offers and track their project.`
    : `${name} should download the Sunrun Customer App to receive & send messages, view their offers, and track their project.`;
  const actionButtonText = isAffiliate
    ? 'Share by email'
    : 'Share by email & messages';
  const isPending = sendStatus === 'pending';

  if (sendStatus === 'success') {
    return createPortal(
      <Snackbar
        open
        onClose={handleDismiss}
        message="Offer presentation sent successfully!"
        type="success"
        colorScheme="dark"
      />,
      document.body,
    );
  }

  return createPortal(
    <Modal
      onClose={handleDismiss}
      testId="share-presentation-modal"
      title="Share Presentation"
      subtitle="The customer will be able to view their offer presentation in the Sunrun Customer App."
      customModalWidth={820}
      secondaryButton={{
        text: 'Cancel',
        onClick: handleDismiss,
        testId: 'share-presentation-modal-cancel-button',
        disabled: isPending,
      }}
      primaryButton={{
        text: actionButtonText,
        onClick: handleSend,
        testId: 'share-presentation-modal-send-email-button',
        isLoading: isPending,
        disabled: isPending,
      }}>
      <StyledBanner show={true} showClose={false}>
        {bannerText}
      </StyledBanner>
      <Card>
        <Typography variant="p">{name}</Typography>
        <Typography variant="p" color={tokens.TINTS_OFF_WHITE_20}>
          {email}
        </Typography>
      </Card>
      {sendStatus === 'error' && (
        <Typography variant="p" color={tokens.TINTS_ERROR_20}>
          Error sending email
        </Typography>
      )}
    </Modal>,
    document.body,
  );
};

export { SharePresentationModal };
